var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../skeleton/skeleton';
import styles from './toggle.styles';
import { SlotController } from '../../controllers/slot-controller';
/**
 * @slot on - Custom on icon for custom off state
 * @slot off - Custom off icon for custom off state
 * @slot unchecked-label - un checked label slot
 * @slot checked-label - Checked label slot
 * @slot field-note - field-note slot
 * @fires bl-toggle-change - Emitted when a element toggled
 * @csspart base - The component's base wrapper.
 * @csspart skeleton-label - The component's skeleton for label.
 * @csspart skeleton-wrapper - The component's skeleton wrapper.
 * @csspart skeleton-body - The component's skeleton body.
 * @csspart skeleton-first - The component's skeleton-first wrapper.
 * @csspart skeleton-second - The component's skeleton-second wrapper.
 * @csspart skeleton-field-note - The component's skeleton field-note.
 * @csspart part-label - The component's label wrapper.
 * @csspart input - The component's input element.
 * @csspart wrapper - The component's wrapper.
 * @csspart checkbox - The component's input.
 * @csspart handle - The component's handle wrapper.
 * @csspart on-wrap - The component's on-wrap element.
 * @csspart part-on - The component's on slot.
 * @csspart off-wrap - The component's off-wrap element.
 * @csspart part-off - The component's off slot.
 * @csspart value - The component's value.
 * @csspart part-checked-label - The component's checked-label slot.
 * @csspart part-unchecked-label - The component's unchecked-label slot.
 * @csspart field-note-wrap - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 */
export class BlocksToggle extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Checked state
         * - If true, sets toggle the checkbox to active
         * - If false, sets toggle the checkbox to active
         */
        this.checked = false;
        /**
         * Label - Displayed above toggle component
         */
        this.label = 'Toggle';
        this.slots = new SlotController(this, 'on', 'off', 'unchecked-label', 'checked-label', 'field-note');
        this.internals_ = this.attachInternals?.();
    }
    static { this.formAssociated = true; }
    static { this.styles = styles; }
    get hasFieldNoteSlot() {
        return this.slots.hasSlotted('field-note');
    }
    get hasUncheckedLabelSlot() {
        return this.slots.hasSlotted('unchecked-label');
    }
    get hasCheckedLabelSlot() {
        return this.slots.hasSlotted('checked-label');
    }
    /**
     * Associates the control with a form element
     */
    get form() {
        return this.internals_?.form;
    }
    /**
     * Connected callback lifecycle
     * 1) Autogenerates ids to make this accessible if these properties aren't provided
     */
    connectedCallback() {
        super.connectedCallback();
        this.fieldId = this.fieldId || nanoid(); /* 1 */
    }
    updated(_changedProperties) {
        if (_changedProperties.has('value')) {
            this.internals_?.setFormValue(this.checked ? String(this.checked) : String(false));
        }
    }
    /**
     * Trigger toggle event
     */
    triggerToggleEvent() {
        this.checked = !this.checked;
        this.shadowRoot?.querySelector('.cat-c-toggle__checkbox')?.focus();
        this.dispatch({ eventName: 'bl-toggle-change', detailObj: { checked: this.checked } });
    }
    /**
     * Handle on keydown events
     * 1) If the Enter key is pressed, then check the checkbox
     */
    handleKeydown(e) {
        if (this.readonly && e.key !== KEYS.TAB) {
            e.preventDefault();
            return;
        }
        if (e.key === KEYS.ENTER) {
            this.triggerToggleEvent();
        }
    }
    hasFieldNote() {
        return this.fieldNote || this.hasFieldNoteSlot;
    }
    setAriaDescribedBy() {
        if (this.hasFieldNote()) {
            this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
        }
        if (this.hasCheckedLabelSlot || this.hasUncheckedLabelSlot) {
            this.valueLabelId = nanoid();
        }
    }
    handleAriaDescribedBy() {
        if (this.valueLabelId && this.ariaDescribedBy) {
            return this.valueLabelId + ' ' + this.ariaDescribedBy;
        }
        else if (this.valueLabelId || this.ariaDescribedBy) {
            return this.valueLabelId || this.ariaDescribedBy;
        }
        else {
            return nothing;
        }
    }
    /**
     * Render Loading State
     */
    renderLoading() {
        return html `<div part="base" class="cat-c-toggle__loader">
      ${!this.hideLabel
            ? html `<cat-skeleton
            part="skeleton-label"
            exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
            width="100px"
            arialabel=""
          ></cat-skeleton>`
            : nothing}
      <div part="skeleton-wrapper" class="cat-c-toggle__loader-wrapper">
        <div part="skeleton-body" class="cat-c-toggle__loader-body">
          <cat-skeleton part="skeleton-first" exportparts=${this.getExportParts('skeleton-first', 'skeleton')} width="50px"></cat-skeleton>
          <cat-skeleton
            part="skeleton-second"
            exportparts=${this.getExportParts('skeleton-second', 'skeleton')}
            width="42px"
            arialabel=""
          ></cat-skeleton>
        </div>
        ${this.fieldNote || this.hasFieldNoteSlot
            ? html `<cat-skeleton
              part="skeleton-field-note"
              exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
              width="100px"
              arialabel=""
            ></cat-skeleton>`
            : nothing}
      </div>
    </div>`;
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-toggle', {
            'cat-is-checked': this.checked === true,
            'cat-is-disabled': this.disabled === true,
            'cat-is-readonly': this.readonly === true,
            'cat-c-toggle-hidden--label': this.hideLabel,
            'cat-c-toggle--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
            'cat-c-toggle--has-on': this.slots.hasSlotted('on'),
            'cat-c-toggle--has-off': this.slots.hasSlotted('off'),
            'cat-c-toggle--has-checked-label': this.hasCheckedLabelSlot,
            'cat-c-toggle--has-unchecked-label': this.hasUncheckedLabelSlot
        });
        if (this.isLoading) {
            return this.renderLoading();
        }
        this.setAriaDescribedBy();
        return html `<div part="base" class="${componentClassNames}">
      <label part="part-label" class="cat-c-toggle-label" for=${ifDefined(this.fieldId)}> ${this.label} </label>
      <div part="wrapper" class="cat-c-toggle-wrapper">
        <input
          part="input"
          class="cat-c-toggle__checkbox"
          type="checkbox"
          id="${ifDefined(this.fieldId)}"
          role="switch"
          aria-describedby="${ifDefined(this.handleAriaDescribedBy())}"
          aria-checked="${this.checked}"
          aria-readonly="${ifDefined(this.readonly)}"
          name="${ifDefined(this.name)}"
          value="${ifDefined(this.value)}"
          .checked=${this.checked}
          ?disabled=${this.disabled}
          @change=${this.triggerToggleEvent}
          @keydown=${this.handleKeydown}
        />
        <div part="handle" class="cat-c-toggle__handle" @click=${this.triggerToggleEvent}>
          <span part="on-wrap" class="cat-c-toggle__on" aria-hidden="${true}">
            <slot part="part-on" name="on"></slot>
          </span>
          <span part="off-wrap" class="cat-c-toggle__off" aria-hidden="${true}">
            <slot part="part-off" name="off"></slot>
          </span>
        </div>
        <span part="value" class="cat-c-toggle__value cat-c__label--checked" id=${ifDefined(this.checked ? this.valueLabelId : undefined)}>
          <slot part="part-checked-label" name="checked-label"></slot>
        </span>
        <span part="value" class="cat-c-toggle__value cat-c__label--unchecked" id=${ifDefined(!this.checked ? this.valueLabelId : undefined)}>
          <slot part="part-unchecked-label" name="unchecked-label"></slot>
        </span>
      </div>
      <cat-field-note
        part="field-note-wrap"
        exportparts=${this.getExportParts('field-note')}
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.ariaDescribedBy)}
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
    </div>`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksToggle.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToggle.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToggle.prototype, "checked", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "ariaDescribedBy", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "fieldId", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "label", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToggle.prototype, "hideLabel", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "name", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "value", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToggle.prototype, "isLoading", void 0);
__decorate([
    property({ type: String })
], BlocksToggle.prototype, "fieldNote", void 0);
if (customElements.get('cat-toggle') === undefined) {
    customElements.define('cat-toggle', BlocksToggle);
}
